import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Paper,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Alert,
  Snackbar,
  Chip,
  InputAdornment
, MenuItem
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { Add, Edit, Delete, Search } from '@mui/icons-material';
import { positionAPI, companyAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';

const ENTITY_TYPES = [
  'IT',
  'Logistics'
];

const Positions = () => {
  const [positions, setPositions] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [filteredCompanies, setFilteredCompanies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentPosition, setCurrentPosition] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [formData, setFormData] = useState({
    title: '',
    entity: '',
    company: '',
    department: '',
    description: '',
    color: '#1976d2'
  });
  const { isAdmin } = useAuth();

  useEffect(() => {
    fetchPositions();
    fetchCompanies();
  }, [searchTerm]);

  useEffect(() => {
    if (formData.entity) {
      const filtered = companies.filter(c => c.entity === formData.entity);
      setFilteredCompanies(filtered);
      if (formData.company) {
        const currentCompany = companies.find(c => c._id === formData.company);
        if (currentCompany && currentCompany.entity !== formData.entity) {
          setFormData(prev => ({ ...prev, company: '' }));
        }
      }
    } else {
      setFilteredCompanies(companies);
    }
  }, [formData.entity, companies]);

  const fetchPositions = async () => {
    setLoading(true);
    try {
      const response = await positionAPI.getAll({ search: searchTerm, limit: 100 });
      setPositions(response.data.data);
    } catch (error) {
      showSnackbar('Failed to fetch positions', 'error');
    }
    setLoading(false);
  };

  const fetchCompanies = async () => {
    try {
      const response = await companyAPI.getAll({ isActive: 'true' });
      setCompanies(response.data.data);
    } catch (error) {
      console.error('Failed to fetch companies', error);
    }
  };

  const handleOpenDialog = (position = null) => {
    if (position) {
      setEditMode(true);
      setCurrentPosition(position);
      setFormData({
        title: position.title,
        entity: position.entity || '',
        company: position.company?._id || position.company || '',
        department: position.department || '',
        description: position.description || '',
        color: position.color || '#1976d2'
      });
    } else {
      setEditMode(false);
      setCurrentPosition(null);
      setFormData({
        title: '',
        entity: '',
        company: '',
        department: '',
        description: '',
        color: '#1976d2'
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setFormData({
      title: '',
      entity: '',
      company: '',
      department: '',
      description: '',
      color: '#1976d2'
    });
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editMode) {
        await positionAPI.update(currentPosition._id, formData);
        showSnackbar('Position updated successfully', 'success');
      } else {
        await positionAPI.create(formData);
        showSnackbar('Position created successfully', 'success');
      }
      handleCloseDialog();
      fetchPositions();
    } catch (error) {
      showSnackbar(error.response?.data?.message || 'Operation failed', 'error');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this position?')) {
      try {
        await positionAPI.delete(id);
        showSnackbar('Position deleted successfully', 'success');
        fetchPositions();
      } catch (error) {
        showSnackbar(error.response?.data?.message || 'Delete failed', 'error');
      }
    }
  };

  const showSnackbar = (message, severity) => {
    setSnackbar({ open: true, message, severity });
  };

  const columns = [
    { 
      field: 'title', 
      headerName: 'Position Title', 
      flex: 1, 
      minWidth: 200,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box
            sx={{
              width: 20,
              height: 20,
              borderRadius: '4px',
              backgroundColor: params.row.color || '#1976d2',
              border: '1px solid #ddd'
            }}
          />
          <Typography>{params.value || 'N/A'}</Typography>
        </Box>
      )
    },
    { 
      field: 'entity', 
      headerName: 'Entity', 
      flex: 1, 
      minWidth: 130,
      renderCell: (params) => (
        params.value ? (
          <Chip
            label={params.value}
            color="primary"
            variant="outlined"
            size="small"
          />
        ) : (
          'N/A'
        )
      )
    },
    { 
      field: 'company', 
      headerName: 'Company', 
      flex: 1, 
      minWidth: 150,
      valueGetter: (params) => params.row.company?.name || 'Entity Only'
    },
    { 
      field: 'department', 
      headerName: 'Department', 
      flex: 1, 
      minWidth: 150,
      valueGetter: (params) => params.row.department || 'N/A'
    },
    { 
      field: 'description', 
      headerName: 'Description', 
      flex: 2, 
      minWidth: 250,
      valueGetter: (params) => params.row.description || 'N/A'
    },
    {
      field: 'isActive',
      headerName: 'Status',
      width: 100,
      renderCell: (params) => (
        <Chip
          label={params.value ? 'Active' : 'Inactive'}
          color={params.value ? 'success' : 'default'}
          size="small"
        />
      )
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 120,
      sortable: false,
      renderCell: (params) => (
        <Box>
          {isAdmin && (
            <>
              <IconButton
                size="small"
                color="primary"
                onClick={() => handleOpenDialog(params.row)}
              >
                <Edit />
              </IconButton>
              <IconButton
                size="small"
                color="error"
                onClick={() => handleDelete(params.row._id)}
              >
                <Delete />
              </IconButton>
            </>
          )}
        </Box>
      )
    }
  ];

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Positions</Typography>
        {isAdmin && (
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => handleOpenDialog()}
          >
            Add Position
          </Button>
        )}
      </Box>

      <Paper sx={{ p: 2, mb: 2 }}>
        <TextField
          fullWidth
          placeholder="Search positions..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            )
          }}
        />
      </Paper>

      <Paper sx={{ height: 600, width: '100%' }}>
        <DataGrid
          rows={positions}
          columns={columns}
          pageSize={10}
          rowsPerPageOptions={[10, 25, 50]}
          loading={loading}
          getRowId={(row) => row._id}
          disableSelectionOnClick
        />
      </Paper>

      {/* Add/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editMode ? 'Edit Position' : 'Add New Position'}</DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              name="title"
              label="Position Title"
              type="text"
              fullWidth
              required
              value={formData.title}
              onChange={handleChange}
            />
            <TextField
              select
              margin="dense"
              name="entity"
              label="Entity Type"
              fullWidth
              required
              value={formData.entity}
              onChange={handleChange}
            >
              {ENTITY_TYPES.map((entity) => (
                <MenuItem key={entity} value={entity}>
                  {entity}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              margin="dense"
              name="company"
              label="Company (Optional)"
              fullWidth
              value={formData.company}
              onChange={handleChange}
              disabled={!formData.entity}
              helperText={!formData.entity ? 'Please select an entity first' : 'Leave empty to post directly under entity'}
            >
              <MenuItem value="">
                <em>None - Post under Entity only</em>
              </MenuItem>
              {filteredCompanies.map((company) => (
                <MenuItem key={company._id} value={company._id}>
                  {company.name}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              margin="dense"
              name="department"
              label="Department"
              type="text"
              fullWidth
              value={formData.department}
              onChange={handleChange}
            />
            <TextField
              margin="dense"
              name="description"
              label="Description"
              type="text"
              fullWidth
              multiline
              rows={3}
              value={formData.description}
              onChange={handleChange}
            />
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                Position Color
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <input
                  type="color"
                  name="color"
                  value={formData.color}
                  onChange={handleChange}
                  style={{
                    width: '80px',
                    height: '56px',
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                />
                <TextField
                  name="color"
                  label="Hex Color"
                  value={formData.color}
                  onChange={handleChange}
                  placeholder="#1976d2"
                  helperText="This color will be used as border for jobs on the website"
                  fullWidth
                />
              </Box>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button type="submit" variant="contained">
              {editMode ? 'Update' : 'Create'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Positions;
